import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  TALK_TO_DESIGNER,
  OUTREACH_NOTES,
  greetingFor,
  isFallbackNumber,
  whatsAppUrl,
} from '../../config/whatsapp';
import { collaborationService } from '../../services/collaboration.service';
import { creatorService } from '../../services/creator.service';
import { trackEvent } from '../../lib/posthog';

/**
 * The WhatsApp brand glyph. Lucide has no WhatsApp icon, and a generic speech
 * bubble loses the one thing this button trades on — a buyer recognising the
 * channel before they read the label.
 */
const WhatsAppGlyph = ({ size = 18, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.2 1.2-1.7 1.2-.5.1-1 .1-1.6-.1-.4-.1-.9-.3-1.5-.6-2.6-1.1-4.3-3.7-4.4-3.9-.1-.2-1-1.4-1-2.6s.6-1.8.9-2.1c.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 1.9c.1.2.1.4 0 .5l-.3.5-.3.3c-.1.1-.3.3-.1.6.2.3.7 1.2 1.6 2 1.1.9 1.9 1.2 2.2 1.3.2.1.4.1.6-.1l.8-1c.2-.2.3-.2.6-.1l1.8.9c.3.1.4.2.5.3.1.2.1.6-.1 1.2z" />
  </svg>
);

type Variant = 'float' | 'inline' | 'icon' | 'link' | 'toggle';

/**
 * The grow/shrink spring. Matches the lookbook's own `SETTLE`, so a button
 * trading places with this one moves at the same rate rather than racing it.
 * Not exported — a shared constant here costs the file its fast refresh.
 */
const TOGGLE_SPRING = { type: 'spring' as const, stiffness: 380, damping: 40, mass: 0.9 };

/** Whoever the buyer is trying to reach. Every field is optional on purpose. */
export interface TalkToDesignerTarget {
  /** The creator's user id. Without it the outreach cannot be recorded. */
  id?: string;
  name?: string;
  /**
   * Their saved WhatsApp number. Blank routes to AnkaraGrid Africa. Leave it
   * `undefined` and, given an `id`, the component looks it up — the archive
   * and edits feeds don't carry the number, only the profile endpoint does.
   */
  whatsappNumber?: string | null;
}

interface TalkToDesignerProps {
  /**
   *   float  — the persistent pill on a discovery surface.
   *   inline — beside a page's primary CTA, label visible.
   *   icon   — the same thing where there is only room for a circle.
   *   link   — bare text, where a bordered button would compete with the step
   *            the buyer is already on.
   *   toggle — a circle that grows into a labelled pill when `expanded`, for
   *            sharing a row with a primary CTA that collapses as it grows.
   */
  variant?: Variant;
  /**
   * `toggle` only. Collapsed, the first tap calls `onExpand` instead of
   * following the link — the label has to be readable before it can be
   * chosen. Once expanded, it opens WhatsApp like any other variant.
   */
  expanded?: boolean;
  onExpand?: () => void;
  /**
   * The designer being reached out to. Omit it on a surface with no single
   * creator (home, the edits index) and the tap reaches AnkaraGrid Africa.
   */
  designer?: TalkToDesignerTarget;
  /**
   * What this conversation is about, recorded against the request rather than
   * typed into WhatsApp. Use the builders in `OUTREACH_NOTES`.
   */
  note?: string;
  /** Overrides the label. `icon` uses it as the accessible name. */
  label?: string;
  /** Where the tap came from, for analytics. */
  surface: string;
  /**
   * `light` for a white page, `dark` for a full-bleed photo surface like the
   * lookbook. A tone rather than a className override, so the two never fight
   * over which utility class wins.
   */
  tone?: 'light' | 'dark';
  /**
   * `pill` matches a rounded primary CTA sitting next to it, as in the
   * lookbook. A prop rather than a className override, because two competing
   * border-radius utilities are settled by stylesheet order, not by which one
   * the caller passed last.
   */
  shape?: 'rounded' | 'pill';
  className?: string;
}

/**
 * One tap from any buyer surface into WhatsApp — the designer's own number
 * when they have saved one, ours when they have not.
 *
 * The prefill is only a greeting. What the buyer wants is recorded through
 * `collaboration/requests` (the same endpoint `RequestCollabModal` has always
 * used), so the designer sees the context in their dashboard whether or not
 * the buyer keeps a word of the prefilled text.
 *
 * Always a real `<a>`: it leaves the app, so it must middle-click, long-press
 * and open in a new tab like any other link.
 */
const TalkToDesigner: React.FC<TalkToDesignerProps> = ({
  variant = 'inline',
  expanded = true,
  onExpand,
  designer,
  note = OUTREACH_NOTES.general,
  label = TALK_TO_DESIGNER,
  surface,
  tone = 'light',
  shape = 'rounded',
  className = '',
}) => {
  /**
   * Resolve the creator's own number when the surface could not supply it.
   * `getCreatorById` is deduped and cached for five minutes, and the archive
   * post page already warms it, so this is usually a cache hit. Until it
   * lands the link points at AnkaraGrid Africa, so an immediate tap still
   * reaches a human rather than nothing.
   */
  const [lookedUp, setLookedUp] = React.useState<string | null>(null);
  const needsLookup = !!designer?.id && designer.whatsappNumber === undefined;

  React.useEffect(() => {
    if (!needsLookup || !designer?.id) return;
    let live = true;
    creatorService
      .getCreatorById(designer.id)
      .then((c) => {
        if (live) setLookedUp(c?.socialMedia?.whatsapp_number ?? null);
      })
      .catch(() => {
        /* no number to be had — the fallback already covers it */
      });
    return () => {
      live = false;
    };
  }, [needsLookup, designer?.id]);

  const number = designer?.whatsappNumber ?? lookedUp;
  const reachesUs = isFallbackNumber(number);
  const href = whatsAppUrl(greetingFor(designer?.name, reachesUs), number);

  const onClick = () => {
    try {
      trackEvent('talk_to_designer_clicked', {
        surface,
        variant,
        designer_id: designer?.id ?? null,
        designer_name: designer?.name ?? null,
        /* Tells us how often buyers land on us instead of the creator — the
           number to watch if designers are not saving a WhatsApp number. */
        routed_to_fallback: reachesUs,
      });
    } catch {
      /* analytics must never block the link */
    }

    /* Record who was reached out to and what about. Fire-and-forget: the tab
       is already opening, and a failed log must not cost the conversation. */
    if (designer?.id) {
      collaborationService
        .createCollaborationRequest({
          toUserId: designer.id,
          message: note,
          platform: 'whatsapp',
          deviceType:
            window.innerWidth < 768 ||
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
              ? 'mobile'
              : 'desktop',
        })
        .catch(() => {
          /* already logged by the service */
        });
    }
  };

  const shared = {
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick,
    'aria-label': `${label} on WhatsApp`,
  };

  if (variant === 'toggle') {
    return (
      <motion.a
        {...shared}
        layout
        transition={TOGGLE_SPRING}
        /* Collapsed it is a 44px target with no label, so tapping it reveals
           what it does rather than firing it. Screen readers get the label
           either way, so for them it stays a one-tap link — hence the extra
           `aria-expanded` rather than a swap of the accessible name. */
        aria-expanded={expanded}
        onClick={(e) => {
          if (!expanded && onExpand) {
            e.preventDefault();
            onExpand();
            return;
          }
          onClick();
        }}
        whileTap={{ scale: 0.95 }}
        className={`flex-shrink-0 inline-flex items-center justify-center gap-2 h-11 rounded-full border overflow-hidden ${
          expanded ? 'flex-1 px-4' : 'w-11'
        } ${
          tone === 'dark'
            ? 'border-white/30 text-white hover:bg-white/10'
            : 'border-gray-200 text-gray-900 hover:bg-gray-50'
        } ${className}`}
      >
        <motion.span layout="position" className="flex-shrink-0 text-[#25D366] leading-none">
          <WhatsAppGlyph size={19} />
        </motion.span>
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.span
              key="label"
              layout="position"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              className="text-xs font-bold uppercase tracking-wide whitespace-nowrap"
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.a>
    );
  }

  if (variant === 'float') {
    return (
      <a
        {...shared}
        /* Sits below the mobile tab bar's z-50 so it can never cover
           navigation, and clears its 4rem height plus the safe area. */
        className={`fixed right-4 bottom-20 md:right-6 md:bottom-6 z-40 inline-flex items-center gap-2.5 pl-2 pr-5 py-2 rounded-full bg-[#111111] text-white shadow-[0_10px_26px_rgba(17,17,17,0.32)] hover:bg-black active:scale-[0.97] transition-all ${className}`}
      >
        <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[#25D366] text-[#0B0B0B]">
          <WhatsAppGlyph size={18} />
        </span>
        <span className="text-[13px] font-bold whitespace-nowrap">{label}</span>
      </a>
    );
  }

  if (variant === 'icon') {
    /* The glyph stays WhatsApp green on both tones — a white one reads as a
       generic call button and loses the recognition the icon is here for. */
    const iconTone =
      tone === 'dark'
        ? 'w-10 h-10 border-white/30 text-[#25D366] hover:bg-white/10'
        : 'w-12 h-12 border-gray-200 text-[#25D366] hover:border-gray-300 hover:bg-gray-50';
    return (
      <a
        {...shared}
        title={label}
        className={`flex-shrink-0 flex items-center justify-center rounded-full border active:scale-95 transition-all ${iconTone} ${className}`}
      >
        <WhatsAppGlyph size={tone === 'dark' ? 18 : 22} />
      </a>
    );
  }

  if (variant === 'link') {
    return (
      <a
        {...shared}
        className={`inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${
          tone === 'dark' ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-900'
        } ${className}`}
      >
        <WhatsAppGlyph size={14} className="text-[#25D366]" />
        {label}
      </a>
    );
  }

  const inlineTone =
    tone === 'dark'
      ? 'border-white/25 bg-white/5 text-white hover:bg-white/10'
      : 'border-gray-200 bg-white text-gray-900 hover:border-gray-300 hover:bg-gray-50';
  const inlineShape =
    shape === 'pill' ? 'px-4 py-2.5 rounded-full text-xs' : 'px-4 py-3 rounded-lg text-[13px]';
  return (
    <a
      {...shared}
      className={`inline-flex items-center justify-center gap-2 border font-semibold active:scale-[0.98] transition-all ${inlineShape} ${inlineTone} ${className}`}
    >
      <WhatsAppGlyph size={17} className="text-[#25D366]" />
      {label}
    </a>
  );
};

export default TalkToDesigner;
